import React, { useState, useEffect } from 'react'
import { getServerSessionId } from '../../lib/serverSession'
import {
  hashPassword,
  validatePasswordStrength,
  getPasswordStrengthScore,
  generateSecurePassword
} from '../../lib/passwordSecurity'
import { logAdminAction } from '../../lib/adminAuth'

interface User {
  id: number
  username: string
  email: string
  password_hash: string
  full_name: string | null
  phone: string | null
  address: string | null
  is_active: boolean
  is_verified: boolean
  country_of_residence: string | null
  role: string
  is_admin: boolean
  created_at: string
  updated_at: string
}

interface UserEditModalProps {
  user: User
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  adminUser?: any
}

interface FormData {
  username: string
  email: string
  full_name: string
  phone: string
  address: string
  is_active: boolean
  role: string
  country_of_residence: string
  password: string
  confirmPassword: string
}

interface ValidationErrors {
  [key: string]: string
}

export const UserEditModal: React.FC<UserEditModalProps> = ({
  user,
  isOpen,
  onClose,
  onSave,
  adminUser
}) => {
  const [formData, setFormData] = useState<FormData>({
    username: '',
    email: '',
    full_name: '',
    phone: '',
    address: '',
    is_active: true,
    role: 'user',
    country_of_residence: '',
    password: '',
    confirmPassword: ''
  })

  const [errors, setErrors] = useState<ValidationErrors>({})
  const [loading, setSaving] = useState(false)
  const [showPasswordFields, setShowPasswordFields] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [passwordStrength, setPasswordStrength] = useState<any>(null)
  const [showGeneratedPassword, setShowGeneratedPassword] = useState(false)
  const [showPassword, setShowPassword] = useState(true)
  const [showConfirmPassword, setShowConfirmPassword] = useState(true)
	 const [emailChangeReason, setEmailChangeReason] = useState('')
	 const [emailChangeConfirmation, setEmailChangeConfirmation] = useState('')
  const [beneficiaries, setBeneficiaries] = useState<any[]>([])
  const [loadingBeneficiaries, setLoadingBeneficiaries] = useState(false)
  // BSC Wallet state (Johan only)
  const isJohan = adminUser?.email?.toLowerCase() === 'johan@beaversden.co.za'
	 const emailChanged = formData.email.trim().toLowerCase() !== (user?.email || '').trim().toLowerCase()
  const [bscWallet, setBscWallet] = useState<any>(null)
  const [walletLoading, setWalletLoading] = useState(false)
  const [walletNote, setWalletNote] = useState('')
  const [walletRemoving, setWalletRemoving] = useState(false)
  const [walletConfirm, setWalletConfirm] = useState(false)
  const [walletMsg, setWalletMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  // Add-wallet state (Johan only) — manually restore/add a locked BSC wallet
  const [walletAddOpen, setWalletAddOpen] = useState(false)
  const [walletAddAddress, setWalletAddAddress] = useState('')
  const [walletAddType, setWalletAddType] = useState<'decentralized' | 'centralized'>('decentralized')
  const [walletAddReason, setWalletAddReason] = useState('')
  const [walletAdding, setWalletAdding] = useState(false)
  // Change-wallet state (Johan only) — change an active locked BSC wallet address in place
  const [walletChangeOpen, setWalletChangeOpen] = useState(false)
  const [walletChangeAddress, setWalletChangeAddress] = useState('')
  const [walletChangeReason, setWalletChangeReason] = useState('')
  const [walletChanging, setWalletChanging] = useState(false)

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        full_name: user.full_name || '',
        phone: user.phone || '',
        address: user.address || '',
        is_active: user.is_active,
        role: user.role || 'user',
        country_of_residence: user.country_of_residence || '',
        password: '',
        confirmPassword: ''
      })
	      setEmailChangeReason('')
	      setEmailChangeConfirmation('')
      // Load beneficiaries for this user
      loadBeneficiaries()
      if (isJohan) loadBscWallet()
    }
  }, [user])

  const loadBeneficiaries = async () => {
    if (!user?.id) return
    try {
      setLoadingBeneficiaries(true)
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/user-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({ operation: 'load_beneficiaries', data: { userId: user.id } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)
      setBeneficiaries(result.beneficiaries || [])
    } catch (error) {
      console.error('Error loading beneficiaries:', error)
    } finally {
      setLoadingBeneficiaries(false)
    }
  }

  const loadBscWallet = async () => {
    if (!user?.id) return
    setWalletLoading(true)
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/user-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({ operation: 'get_bsc_wallet', data: { userId: user.id } })
      })
      const result = await response.json()
      if (result.success) setBscWallet(result.wallet || null)
    } catch { /* ignore */ }
    finally { setWalletLoading(false) }
  }

  const removeBscWallet = async () => {
    if (!user?.id || !walletNote.trim() || walletNote.trim().length < 5) return
    setWalletRemoving(true); setWalletMsg(null)
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/user-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({ operation: 'remove_bsc_wallet', data: { userId: user.id, reason: walletNote.trim() } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)
      setWalletMsg({ type: 'success', text: `BSC wallet removed: ${result.removed_wallet}` })
      setBscWallet(null); setWalletNote(''); setWalletConfirm(false)
    } catch (err: any) {
      setWalletMsg({ type: 'error', text: err.message })
    } finally { setWalletRemoving(false) }
  }

  const changeBscWallet = async () => {
    if (!user?.id) return
    const addr = walletChangeAddress.trim()
    if (!/^0x[a-fA-F0-9]{40}$/.test(addr)) {
      setWalletMsg({ type: 'error', text: 'Enter a valid BSC address (0x + 40 hex characters).' }); return
    }
    if (walletChangeReason.trim().length < 5) {
      setWalletMsg({ type: 'error', text: 'A reason of at least 5 characters is required.' }); return
    }
    setWalletChanging(true); setWalletMsg(null)
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/user-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({ operation: 'update_bsc_wallet', data: { userId: user.id, walletAddress: addr, reason: walletChangeReason.trim() } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)
      setWalletMsg({ type: 'success', text: `BSC wallet changed to: ${result.new_wallet}` })
      setWalletChangeOpen(false); setWalletChangeAddress(''); setWalletChangeReason('')
      loadBscWallet()
    } catch (err: any) {
      setWalletMsg({ type: 'error', text: err.message })
    } finally { setWalletChanging(false) }
  }

  const addBscWallet = async () => {
    if (!user?.id) return
    const addr = walletAddAddress.trim()
    if (!/^0x[a-fA-F0-9]{40}$/.test(addr)) {
      setWalletMsg({ type: 'error', text: 'Enter a valid BSC address (0x + 40 hex characters).' }); return
    }
    if (walletAddReason.trim().length < 5) {
      setWalletMsg({ type: 'error', text: 'A reason of at least 5 characters is required.' }); return
    }
    setWalletAdding(true); setWalletMsg(null)
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/user-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({ operation: 'add_bsc_wallet', data: { userId: user.id, walletAddress: addr, walletType: walletAddType, reason: walletAddReason.trim() } })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)
      setWalletMsg({ type: 'success', text: `BSC wallet added: ${result.added_wallet}` })
      setWalletAddOpen(false); setWalletAddAddress(''); setWalletAddReason(''); setWalletAddType('decentralized')
      loadBscWallet()
    } catch (err: any) {
      setWalletMsg({ type: 'error', text: err.message })
    } finally { setWalletAdding(false) }
  }

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {}

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required'
    } else if (formData.username.length < 2) {
      newErrors.username = 'Username must be at least 2 characters'
    } else if (formData.username.length > 25) {
      newErrors.username = 'Username must be 25 characters or less'
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
	    if (emailChanged && !isJohan) {
	      newErrors.email = 'Only Johan may change user email addresses'
	    }
	    if (emailChanged && isJohan) {
	      if (emailChangeReason.trim().length < 10) newErrors.emailChangeReason = 'Reason must be at least 10 characters'
	      if (emailChangeConfirmation !== 'I CONFIRM USER EMAIL CHANGE') newErrors.emailChangeConfirmation = 'Type the exact confirmation phrase'
	    }

    // Full name validation
    if (formData.full_name && formData.full_name.length > 255) {
      newErrors.full_name = 'Full name must be less than 255 characters'
    }

    // Phone validation
    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number'
    }

    // Password validation (only if changing password)
    if (showPasswordFields) {
      if (!formData.password) {
        newErrors.password = 'Password is required when changing password'
      } else {
        const passwordValidation = validatePasswordStrength(formData.password, formData.username)
        if (!passwordValidation.valid) {
          newErrors.password = passwordValidation.errors[0] // Show first error
        }
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match'
      }
    }

    // Country code validation
    if (formData.country_of_residence && formData.country_of_residence.length !== 3) {
      newErrors.country_of_residence = 'Country code must be 3 characters (ISO 3166-1 alpha-3)'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Update password strength meter for password field
    if (field === 'password' && typeof value === 'string') {
      const score = getPasswordStrengthScore(value)
      setPasswordStrength({
        score,
        label: score >= 80 ? 'Strong' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'Weak',
        color: score >= 80 ? 'green' : score >= 60 ? 'yellow' : score >= 40 ? 'orange' : 'red'
      })
    }

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    // Show confirmation dialog for sensitive changes
    const sensitiveChanges =
      formData.is_active !== user.is_active ||
	      showPasswordFields ||
	      emailChanged

    if (sensitiveChanges && !showConfirmDialog) {
      setShowConfirmDialog(true)
      return
    }

    await saveUser()
  }

  const saveUser = async () => {
    try {
      setSaving(true)
      setShowConfirmDialog(false)
      const sessionId = getServerSessionId()

      // Prepare update data
      const updateData: any = {
        username: formData.username,
	        email: isJohan ? formData.email : user.email,
        full_name: formData.full_name || null,
        phone: formData.phone || null,
        address: formData.address || null,
        is_active: formData.is_active,
        role: formData.role,
        country_of_residence: formData.country_of_residence || null,
        updated_at: new Date().toISOString()
      }

      // Hash password if changing
      if (showPasswordFields && formData.password) {
        console.log('🔐 Admin changing user password - hashing with bcrypt...')
        updateData.password_hash = await hashPassword(formData.password)
        // Send plain password separately for Supabase Auth sync (server-side only, never stored)
        updateData._plain_password = formData.password
        console.log('✅ Password hashed successfully for admin password change')
      }

      // Check for duplicate username/email via API
      const checkResponse = await fetch('/api/admin/user-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({
          operation: 'check_duplicates',
          data: { username: formData.username, email: formData.email, excludeId: user.id }
        })
      })
      const checkResult = await checkResponse.json()
      if (!checkResult.success) throw new Error(checkResult.error)

      if (checkResult.existingUsers && checkResult.existingUsers.length > 0) {
        const duplicateUsername = checkResult.existingUsers.find((u: any) => u.username === formData.username)
        const duplicateEmail = checkResult.existingUsers.find((u: any) => u.email === formData.email)

        if (duplicateUsername) {
          setErrors({ username: 'Username already exists' })
          return
        }
        if (duplicateEmail) {
          setErrors({ email: 'Email already exists' })
          return
        }
      }

      // Update user via API
      const updateResponse = await fetch('/api/admin/user-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
	        body: JSON.stringify({
	          operation: 'update_user',
	          data: {
	            userId: user.id,
	            updateData,
	            emailChangeReason: emailChanged ? emailChangeReason.trim() : undefined,
	            emailChangeConfirmation: emailChanged ? emailChangeConfirmation : undefined
	          }
	        })
      })
      const updateResult = await updateResponse.json()
      if (!updateResult.success) throw new Error(updateResult.error)

      // Log the change (audit trail)
      await logAdminAction(
        'current_admin@example.com', // Would get from context
        'UPDATE_USER',
        'user',
        user.id.toString(),
        {
          fields_changed: Object.keys(updateData),
          password_changed: showPasswordFields && formData.password ? true : false
        },
        {
          username: user.username,
          email: user.email,
          is_active: user.is_active
        },
        {
          username: updateData.username,
          email: updateData.email,
          is_active: updateData.is_active
        }
      )

      onSave()
      onClose()
    } catch (err: any) {
      console.error('Error saving user:', err)
      setErrors({ general: err.message || 'Failed to save user' })
    } finally {
      setSaving(false)
    }
  }

  const generatePassword = () => {
    const newPassword = generateSecurePassword(16)
    setFormData(prev => ({
      ...prev,
      password: newPassword,
      confirmPassword: newPassword
    }))
    const score = getPasswordStrengthScore(newPassword)
    setPasswordStrength({
      score,
      label: 'Strong',
      color: 'green'
    })
    setShowGeneratedPassword(true)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-white">
                ✏️ Edit User: {user.full_name || user.username}
              </h2>
              <p className="text-white text-sm">
                User ID: {user.id} | Created: {new Date(user.created_at).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <p className="text-red-400">❌ {errors.general}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
              👤 Basic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username *
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none ${
                    errors.username ? 'border-red-500' : 'border-gray-600 focus:border-yellow-500'
                  }`}
                  placeholder="Enter username"
                />
                {errors.username && (
                  <p className="text-red-400 text-sm mt-1">{errors.username}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
	                  disabled={!isJohan}
                  className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none ${
                    errors.email ? 'border-red-500' : 'border-gray-600 focus:border-yellow-500'
	                  } ${!isJohan ? 'opacity-70 cursor-not-allowed' : ''}`}
                  placeholder="Enter email address"
                />
	                {!isJohan && (
	                  <p className="text-yellow-400 text-xs mt-1">Email changes are restricted to Johan only.</p>
	                )}
	                {isJohan && emailChanged && (
	                  <div className="mt-3 space-y-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3">
	                    <p className="text-yellow-300 text-xs font-semibold">Johan-only email change: reason and typed confirmation required.</p>
	                    <textarea
	                      value={emailChangeReason}
	                      onChange={(e) => setEmailChangeReason(e.target.value)}
	                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
	                      rows={2}
	                      placeholder="Reason for changing this user's email"
	                    />
	                    {errors.emailChangeReason && <p className="text-red-400 text-sm">{errors.emailChangeReason}</p>}
	                    <input
	                      type="text"
	                      value={emailChangeConfirmation}
	                      onChange={(e) => setEmailChangeConfirmation(e.target.value)}
	                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
	                      placeholder="I CONFIRM USER EMAIL CHANGE"
	                    />
	                    {errors.emailChangeConfirmation && <p className="text-red-400 text-sm">{errors.emailChangeConfirmation}</p>}
	                  </div>
	                )}
                {errors.email && (
                  <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none ${
                    errors.full_name ? 'border-red-500' : 'border-gray-600 focus:border-yellow-500'
                  }`}
                  placeholder="Enter full name"
                />
                {errors.full_name && (
                  <p className="text-red-400 text-sm mt-1">{errors.full_name}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none ${
                    errors.phone ? 'border-red-500' : 'border-gray-600 focus:border-yellow-500'
                  }`}
                  placeholder="Enter phone number"
                />
                {errors.phone && (
                  <p className="text-red-400 text-sm mt-1">{errors.phone}</p>
                )}
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Address
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500"
                placeholder="Enter address"
              />
            </div>

            {/* Country */}
            <div className="md:w-1/2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Country Code (ISO 3166-1 alpha-3)
              </label>
              <input
                type="text"
                value={formData.country_of_residence}
                onChange={(e) => handleInputChange('country_of_residence', e.target.value.toUpperCase())}
                maxLength={3}
                className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none ${
                  errors.country_of_residence ? 'border-red-500' : 'border-gray-600 focus:border-yellow-500'
                }`}
                placeholder="e.g., USA, ZAF, GBR"
              />
              {errors.country_of_residence && (
                <p className="text-red-400 text-sm mt-1">{errors.country_of_residence}</p>
              )}
            </div>
          </div>

          {/* Account Status */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
              🔐 Account Status & Permissions
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {/* Active Status */}
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    className="w-4 h-4 text-yellow-500 bg-gray-800 border-gray-600 rounded focus:ring-yellow-500"
                  />
                  <span className="text-gray-300">Account Active</span>
                </label>

              </div>

              <div>
                {/* Role */}
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  User Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                  <option value="moderator">Moderator</option>
                </select>
              </div>
            </div>
          </div>

          {/* Password Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                🔑 Password Management
              </h3>
              <button
                type="button"
                onClick={() => setShowPasswordFields(!showPasswordFields)}
                className="text-yellow-400 hover:text-yellow-300 text-sm font-medium"
              >
                {showPasswordFields ? 'Cancel Password Change' : 'Change Password'}
              </button>
            </div>

            {showPasswordFields && (
              <div className="space-y-4 p-4 bg-gray-800/30 rounded-lg">
                {/* Password Generation */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">Need a secure password?</span>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-500 transition-colors"
                  >
                    🎲 Generate Secure Password
                  </button>
                </div>

                {showGeneratedPassword && (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                    <p className="text-green-400 text-sm">
                      ✅ Secure password generated! Make sure to save it securely.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* New Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      New Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className={`w-full px-3 py-2 pr-10 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none ${
                          errors.password ? 'border-red-500' : 'border-gray-600 focus:border-yellow-500'
                        }`}
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                      >
                        {showPassword ? (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-400 text-sm mt-1">{errors.password}</p>
                    )}

                    {/* Password Strength Meter */}
                    {passwordStrength && formData.password && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-white">Password Strength</span>
                          <span className={`text-sm font-medium ${
                            passwordStrength.strength === 'strong' ? 'text-green-400' :
                            passwordStrength.strength === 'medium' ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {passwordStrength.text}
                          </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                            style={{ width: `${passwordStrength.percentage}%` }}
                          ></div>
                        </div>
                        {passwordStrength.errors && Array.isArray(passwordStrength.errors) && passwordStrength.errors.length > 0 && (
                          <div className="mt-1">
                            {passwordStrength.errors.map((error: any, index: number) => (
                              <p key={index} className="text-sm text-red-400">• {error}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className={`w-full px-3 py-2 pr-10 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none ${
                        errors.confirmPassword ? 'border-red-500' : 'border-gray-600 focus:border-yellow-500'
                      }`}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                    >
                      {showConfirmPassword ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-400 text-sm mt-1">{errors.confirmPassword}</p>
                  )}
                </div>

                </div>

                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-blue-400 mb-2">🔐 Password Requirements</h4>
                  <ul className="text-sm text-white space-y-1">
                    <li>• At least 8 characters long</li>
                    <li>• Contains uppercase and lowercase letters</li>
                    <li>• Contains at least one number</li>
                    <li>• Contains at least one special character</li>
                    <li>• Avoid common passwords and patterns</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Beneficiaries Section (Read-Only) */}
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              👨‍👩‍👧‍👦 Beneficiaries / Next of Kin
            </h3>

            {loadingBeneficiaries ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
                <span className="ml-2 text-white">Loading beneficiaries...</span>
              </div>
            ) : beneficiaries.length > 0 ? (
              <div className="space-y-3">
                {beneficiaries.map((beneficiary: any) => (
                  <div
                    key={beneficiary.id}
                    className={`bg-gray-700/50 rounded-lg p-3 border ${
                      beneficiary.is_primary ? 'border-yellow-500/50' : 'border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-white">{beneficiary.full_name}</span>
                          {beneficiary.is_primary && (
                            <span className="px-2 py-0.5 text-sm font-medium bg-yellow-600 text-white rounded">
                              Primary
                            </span>
                          )}
                          <span className="px-2 py-0.5 text-sm font-medium bg-blue-600 text-white rounded">
                            {beneficiary.percentage_allocation}%
                          </span>
                        </div>
                        <p className="text-white text-sm">{beneficiary.relationship}</p>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1 text-sm text-white">
                          {beneficiary.email && <span>📧 {beneficiary.email}</span>}
                          {beneficiary.phone && <span>📱 {beneficiary.phone}</span>}
                          {beneficiary.id_number && <span>🪪 {beneficiary.id_number}</span>}
                          {beneficiary.city && beneficiary.country && (
                            <span>📍 {beneficiary.city}, {beneficiary.country}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="text-sm text-gray-500 mt-2">
                  Total Allocation: {beneficiaries.reduce((sum: number, b: any) => sum + b.percentage_allocation, 0)}%
                </div>
              </div>
            ) : (
              <div className="bg-gray-800/30 rounded-lg p-4 text-center">
                <p className="text-white">No beneficiaries registered for this user</p>
              </div>
            )}
          </div>

          {/* BSC Wallet Management — Johan Only */}
          {isJohan && (
          <div className="mt-6 pt-4 border-t border-gray-700">
            <h4 className="text-sm font-semibold text-yellow-400 mb-3">🔗 BSC Wallet Management</h4>
            <div className="bg-gray-800/40 rounded-lg p-4 border border-yellow-600/30">
              {walletMsg && (
                <div className={`mb-3 p-2 rounded text-sm ${walletMsg.type === 'success' ? 'bg-green-900/40 text-green-300 border border-green-600/30' : 'bg-red-900/40 text-red-300 border border-red-600/30'}`}>
                  {walletMsg.text}
                </div>
              )}
              {walletLoading ? (
                <p className="text-gray-400 text-sm">Loading wallet...</p>
              ) : bscWallet ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-yellow-400 text-xs font-medium">BSC (BNB Smart Chain)</p>
                      <p className="text-white font-mono text-xs mt-1 break-all">{bscWallet.wallet_address}</p>
                      <p className="text-gray-500 text-xs mt-1">Registered {new Date(bscWallet.registered_at).toLocaleDateString()}</p>
                    </div>
                    <span className="text-green-400 text-xs font-medium">🔒 Active</span>
                  </div>
                  {!walletConfirm && !walletChangeOpen && (
                    <div className="flex gap-2">
                      <button type="button" onClick={() => { setWalletChangeOpen(true); setWalletMsg(null); setWalletChangeAddress(''); setWalletChangeReason(''); }}
                        className="px-3 py-1.5 bg-yellow-700 text-white text-xs rounded hover:bg-yellow-600 transition-colors">
                        ✏️ Change Wallet
                      </button>
                      <button type="button" onClick={() => setWalletConfirm(true)}
                        className="px-3 py-1.5 bg-red-700 text-white text-xs rounded hover:bg-red-600 transition-colors">
                        🗑️ Remove Wallet
                      </button>
                    </div>
                  )}
                  {walletChangeOpen && (
                    <div className="bg-yellow-900/10 border border-yellow-500/30 rounded p-3 mt-2 space-y-2">
                      <p className="text-yellow-300 text-xs font-bold">Change the locked BSC wallet address</p>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">New BSC wallet address (0x...) *</label>
                        <input type="text" value={walletChangeAddress} onChange={e => setWalletChangeAddress(e.target.value)}
                          placeholder="0x0000000000000000000000000000000000000000"
                          className="w-full bg-gray-700 text-white font-mono text-xs px-3 py-1.5 rounded border border-yellow-600/40 focus:border-yellow-400 focus:outline-none" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Reason (required) *</label>
                        <textarea value={walletChangeReason} onChange={e => setWalletChangeReason(e.target.value)}
                          placeholder="e.g. User registered the wrong address and confirmed the correct one..."
                          className="w-full bg-gray-700 text-white text-xs px-3 py-1.5 rounded border border-yellow-600/40 focus:border-yellow-400 focus:outline-none" rows={2} />
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={changeBscWallet}
                          disabled={walletChanging || !/^0x[a-fA-F0-9]{40}$/.test(walletChangeAddress.trim()) || walletChangeReason.trim().length < 5}
                          className="px-3 py-1.5 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-500 disabled:bg-gray-600 disabled:cursor-not-allowed">
                          {walletChanging ? '⏳ Changing...' : '✅ Confirm Change'}
                        </button>
                        <button type="button" onClick={() => { setWalletChangeOpen(false); setWalletChangeAddress(''); setWalletChangeReason(''); }}
                          className="px-3 py-1.5 bg-gray-600 text-white text-xs rounded hover:bg-gray-500">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {walletConfirm && (
                    <div className="bg-red-900/20 border border-red-500/40 rounded p-3 mt-2">
                      <p className="text-red-300 text-xs font-bold mb-2">⚠️ CONFIRM WALLET REMOVAL</p>
                      <p className="text-gray-400 text-xs mb-2">This will remove the locked BSC wallet. The user will need to register a new one.</p>
                      <label className="text-xs text-gray-400 block mb-1">Reason for removal (required) *</label>
                      <textarea value={walletNote} onChange={e => setWalletNote(e.target.value)}
                        placeholder="e.g. Client requested wallet change due to compromised wallet..."
                        className="w-full bg-gray-700 text-white text-xs px-3 py-1.5 rounded border border-red-600/40 focus:border-red-400 focus:outline-none mb-2" rows={2} />
                      <div className="flex gap-2">
                        <button type="button" onClick={removeBscWallet} disabled={walletRemoving || walletNote.trim().length < 5}
                          className="px-3 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-500 disabled:bg-gray-600 disabled:cursor-not-allowed">
                          {walletRemoving ? '⏳ Removing...' : '✅ Confirm Remove'}
                        </button>
                        <button type="button" onClick={() => { setWalletConfirm(false); setWalletNote(''); }}
                          className="px-3 py-1.5 bg-gray-600 text-white text-xs rounded hover:bg-gray-500">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-gray-400 text-sm mb-3">No active BSC wallet registered for this user.</p>
                  {!walletAddOpen ? (
                    <button type="button" onClick={() => { setWalletAddOpen(true); setWalletMsg(null); }}
                      className="px-3 py-1.5 bg-green-700 text-white text-xs rounded hover:bg-green-600 transition-colors">
                      ➕ Add Wallet
                    </button>
                  ) : (
                    <div className="bg-green-900/10 border border-green-500/30 rounded p-3 mt-2 space-y-2">
                      <p className="text-green-300 text-xs font-bold">Manually add a locked BSC wallet</p>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">BSC wallet address (0x...) *</label>
                        <input type="text" value={walletAddAddress} onChange={e => setWalletAddAddress(e.target.value)}
                          placeholder="0x0000000000000000000000000000000000000000"
                          className="w-full bg-gray-700 text-white font-mono text-xs px-3 py-1.5 rounded border border-green-600/40 focus:border-green-400 focus:outline-none" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Wallet type *</label>
                        <select value={walletAddType} onChange={e => setWalletAddType(e.target.value as 'decentralized' | 'centralized')}
                          className="w-full bg-gray-700 text-white text-xs px-3 py-1.5 rounded border border-green-600/40 focus:border-green-400 focus:outline-none">
                          <option value="decentralized">Decentralized (personal self-custody wallet)</option>
                          <option value="centralized">Centralized (exchange wallet)</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Reason (required) *</label>
                        <textarea value={walletAddReason} onChange={e => setWalletAddReason(e.target.value)}
                          placeholder="e.g. Restoring correct wallet after user registered the wrong address..."
                          className="w-full bg-gray-700 text-white text-xs px-3 py-1.5 rounded border border-green-600/40 focus:border-green-400 focus:outline-none" rows={2} />
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={addBscWallet}
                          disabled={walletAdding || !/^0x[a-fA-F0-9]{40}$/.test(walletAddAddress.trim()) || walletAddReason.trim().length < 5}
                          className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed">
                          {walletAdding ? '⏳ Adding...' : '✅ Add Wallet'}
                        </button>
                        <button type="button" onClick={() => { setWalletAddOpen(false); setWalletAddAddress(''); setWalletAddReason(''); }}
                          className="px-3 py-1.5 bg-gray-600 text-white text-xs rounded hover:bg-gray-500">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
              )}
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-60">
          <div className="bg-gray-900 rounded-lg p-6 w-full max-w-md mx-4 border border-yellow-500/30">
            <h3 className="text-lg font-bold text-yellow-400 mb-4">
              ⚠️ Confirm Sensitive Changes
            </h3>
            <p className="text-gray-300 mb-6">
              You are about to make sensitive changes to this user account. This may affect:
            </p>
            <ul className="text-white text-sm space-y-1 mb-6">
              {formData.is_active !== user.is_active && (
                <li>• Account will be {formData.is_active ? 'activated' : 'deactivated'}</li>
              )}
              {showPasswordFields && (
                <li>• User password will be changed</li>
              )}
            </ul>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={saveUser}
                className="px-4 py-2 bg-yellow-500 text-black rounded hover:bg-yellow-400"
              >
                Confirm Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
